import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSession, canManage } from '../lib/session';
import { apiFetch, useResourceMapped } from '../lib/api';
import { useMembers } from '../lib/members';
import { mapArt14, type ApiArt14Row } from '../lib/adapt';
import type { Art14Row } from '../lib/contract';
import { stagesFor, remainingLabel } from '../lib/cra';
import { Badge, Btn } from './ui/primitives';
import { Loading, useToast } from './ui/widgets';
import { BatchContentEditor, type BatchContentPatch } from './BatchContentEditor';
import { NotApplicablePanel, type NotApplicableInput } from './NotApplicablePanel';

const USE_FIXTURES = (import.meta.env.VITE_USE_FIXTURES as string | undefined) !== 'false';

type Filter = 'all' | 'open' | 'art14' | 'overdue' | 'sent' | 'archived';

/**
 * The incidents table shared by the org-wide Incidents page and a product's Reports
 * tab. Owns the filter chips (incl. "Archived"), free-text search, multi-select and
 * the batch EDITOR — select several incidents, write a shared root cause + fix once
 * and apply it to all of them (user request 2026-07-22). `productName` scopes it to
 * one product.
 */
export function IncidentsTable({ productName }: { productName?: string }) {
  const { session } = useSession();
  const navigate = useNavigate();
  const { members } = useMembers(session.organizationId);
  const [filter, setFilter] = useState<Filter>('all');
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [naOpen, setNaOpen] = useState(false);
  const isManager = canManage(session.role);
  const [toastNode, showToast] = useToast();
  const now = Date.now();

  const active = useResourceMapped<ApiArt14Row[], Art14Row[]>(session, '/api/art14', (rows) => rows.map(mapArt14));
  // The archived/deleted set is a different query — fetched only when that chip is on.
  const archived = useResourceMapped<ApiArt14Row[], Art14Row[]>(
    session,
    filter === 'archived' ? '/api/art14?archived=1&fresh=1' : null,
    (rows) => rows.map(mapArt14),
  );
  const isArchivedView = filter === 'archived';
  const source = isArchivedView ? archived : active;

  const rows = useMemo(() => {
    const list = source.data ?? [];
    const q = query.trim().toLowerCase();
    return list.filter((a) => {
      if (productName && a.productName !== productName) return false;
      if (filter === 'open' && a.status !== 'open') return false;
      if (filter === 'sent' && a.status !== 'notified') return false;
      if (filter === 'art14' && !(a.isKev || a.kind === 'incident')) return false;
      if (filter === 'overdue' && a.status !== 'overdue') return false;
      if (q && ![a.productName, a.versionLabel, a.cveId, a.component].some((v) => v?.toLowerCase().includes(q))) return false;
      return true;
    });
  }, [source.data, filter, query, productName]);

  function reloadAll() {
    active.reload();
    if (filter === 'archived') archived.reload();
  }

  const toggle = (id: string) =>
    setSelected((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  const visibleIds = rows.map((r) => r.id);
  const allVisibleSelected = visibleIds.length > 0 && visibleIds.every((id) => selected.has(id));
  const toggleAll = () =>
    setSelected((s) => {
      const n = new Set(s);
      if (allVisibleSelected) visibleIds.forEach((id) => n.delete(id));
      else visibleIds.forEach((id) => n.add(id));
      return n;
    });
  const clearSel = () => {
    setSelected(new Set());
    setEditOpen(false);
    setNaOpen(false);
  };

  async function runBatch(patch: BatchContentPatch & { action?: 'archive' | 'restore' | 'delete' | 'not-applicable'; justificationCode?: string; justification?: string; impactStatement?: string }) {
    const ids = [...selected];
    if (ids.length === 0) return;
    if (USE_FIXTURES) {
      showToast(`Demo: would update ${ids.length} incident(s) — not saved in the demo.`);
      clearSel();
      return;
    }
    setBusy(true);
    try {
      const r = await apiFetch(session, '/api/art14/batch', { method: 'POST', body: JSON.stringify({ ids, patch }) });
      if (!r.ok) { showToast(`Batch update failed (${r.status}).`); return; }
      const data = (await r.json()) as { applied: number; total: number };
      showToast(`Updated ${data.applied} of ${data.total} incident(s).`);
      clearSel();
      reloadAll();
    } catch (e) {
      showToast(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  function applyEdits(patch: BatchContentPatch) {
    if (Object.keys(patch).length === 0) { showToast('Fill at least one field to apply.'); return; }
    void runBatch(patch);
  }

  function applyNotApplicable(data: NotApplicableInput) {
    void runBatch({ action: 'not-applicable', ...data });
  }

  const chips: Array<{ k: Filter; label: string }> = [
    { k: 'all', label: 'All' }, { k: 'open', label: 'Open' }, { k: 'art14', label: 'Art.14' },
    { k: 'overdue', label: 'Overdue' }, { k: 'sent', label: 'Sent' }, { k: 'archived', label: 'Archived' },
  ];

  if (source.loading && !source.data) return <Loading />;

  return (
    <>
      {toastNode}
      <div className="filters">
        <div className="search"><span>⌕</span>
          <input placeholder="Filter by product, CVE, component…" data-testid="incident-search" value={query} onChange={(e) => setQuery(e.target.value)} />
        </div>
        {chips.map((c) => (
          <button key={c.k} className={`chip${filter === c.k ? ' on' : ''}`} data-testid={`chip-${c.k}`} onClick={() => { setFilter(c.k); }}>{c.label}</button>
        ))}
      </div>

      {selected.size > 0 && (
        <div style={{ margin: '10px 0' }}>
          <div className="toolbar" data-testid="batch-bar"
            style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', padding: '10px 12px', background: 'var(--panel, var(--card-bg))', border: '1px solid var(--border)', borderRadius: 8 }}>
            <b data-testid="batch-count" style={{ fontSize: 'calc(13px*var(--fs))' }}>{selected.size} selected</b>
            {isArchivedView ? (
              <>
                <Btn sm variant="primary" disabled={busy} data-testid="batch-restore" onClick={() => void runBatch({ action: 'restore' })}>Restore</Btn>
                <Btn sm disabled={busy} data-testid="batch-clear" onClick={clearSel}>Clear</Btn>
              </>
            ) : (
              <>
                <Btn sm variant="primary" data-testid="batch-edit" onClick={() => { setEditOpen((o) => !o); setNaOpen(false); }}>
                  {editOpen ? 'Close editor' : `Edit ${selected.size} together…`}
                </Btn>
                {isManager && (
                  <Btn sm disabled={busy} data-testid="batch-not-applicable" onClick={() => { setNaOpen((o) => !o); setEditOpen(false); }}>
                    {naOpen ? 'Close' : 'Does not apply…'}
                  </Btn>
                )}
                <Btn sm disabled={busy} data-testid="batch-archive" onClick={() => void runBatch({ action: 'archive' })}>Archive</Btn>
                <Btn sm disabled={busy} data-testid="batch-clear" onClick={clearSel}>Clear</Btn>
              </>
            )}
          </div>

          {!isArchivedView && editOpen && (
            <BatchContentEditor
              count={selected.size}
              busy={busy}
              members={members}
              onApply={applyEdits}
              onCancel={() => setEditOpen(false)}
            />
          )}
          {!isArchivedView && naOpen && (
            <NotApplicablePanel
              count={selected.size}
              busy={busy}
              onApply={applyNotApplicable}
              onCancel={() => setNaOpen(false)}
              note="Only CVE/KEV-linked incidents can carry a VEX; manual free-text incidents are skipped."
            />
          )}
        </div>
      )}

      <div className="tablewrap">
        <table>
          <thead>
            <tr>
              <th style={{ width: 34 }}>
                <input type="checkbox" aria-label="Select all" data-testid="incident-select-all" checked={allVisibleSelected} onChange={toggleAll} />
              </th>
              <th>Product / version</th><th>CVE / cause</th><th>Component</th><th>Type</th><th>Stage</th><th>Status</th><th className="r">{isArchivedView ? 'Removed' : 'Deadline'}</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr><td colSpan={8} className="hint" style={{ padding: 18, textAlign: 'center' }}>
                {isArchivedView ? 'No archived or deleted incidents.' : 'No incidents match this filter.'}
              </td></tr>
            )}
            {rows.map((a) => {
              const stages = stagesFor(a.kind);
              const stage = stages.find((s) => s.key === a.milestone) ?? stages[0];
              const t0 = Date.parse(a.detectedAt);
              const dl = stage.deadline({ t0, fixAvailableAt: a.fixAvailableAt ? Date.parse(a.fixAvailableAt) : null, notified72At: a.notifiedAt ? Date.parse(a.notifiedAt) : null });
              const rem = remainingLabel(dl, now);
              const isSel = selected.has(a.id);
              const href = `/app/incidents/${a.id}${isArchivedView ? '?archived=1' : ''}`;
              return (
                <tr key={a.id} className={`clk${isSel ? ' sel' : ''}`} data-testid="incident-row" data-id={a.id} onClick={() => navigate(href)}>
                  <td onClick={(e) => e.stopPropagation()}>
                    <input type="checkbox" aria-label={`Select ${a.cveId}`} data-testid="incident-select" checked={isSel} onChange={() => toggle(a.id)} />
                  </td>
                  <td data-testid="incident-product">
                    <div style={{ fontWeight: 500 }}>{a.productName}</div>
                    <div className="mono" style={{ color: 'var(--muted)', fontSize: 'calc(11px*var(--fs))' }}>{a.versionLabel}</div>
                  </td>
                  <td className="mono">{a.cveId} {a.isKev && <Badge kind="kev" style={{ fontSize: 'calc(10px*var(--fs))' }}>KEV</Badge>}</td>
                  <td className="mono">{a.component ?? '—'}</td>
                  <td>{a.kind === 'incident' ? <Badge kind="crit">Severe incident</Badge> : a.isKev ? <Badge kind="crit">Art.14</Badge> : <Badge kind="warn">Vuln (manual)</Badge>}</td>
                  <td><Badge kind={a.milestone === '24h' ? 'med' : 'out'}>{a.milestone === 'complete' ? 'done' : a.milestone}</Badge></td>
                  <td>
                    {isArchivedView
                      ? (a.notApplicableAt ? <Badge kind="safe" data-testid="badge-not-applicable">Not applicable</Badge> : a.deletedAt ? <Badge kind="crit">Deleted</Badge> : <Badge kind="out">Archived</Badge>)
                      : (a.status === 'notified' ? <Badge kind="safe">Sent</Badge> : a.reportStatus)}
                  </td>
                  <td className="mono r" style={{ color: isArchivedView ? 'var(--muted)' : rem.overdue ? 'var(--critical)' : a.status === 'notified' ? undefined : 'var(--critical)' }}>
                    {isArchivedView
                      ? (a.notApplicableAt ?? a.deletedAt ?? a.archivedAt ?? '—').toString().slice(0, 10)
                      : a.status === 'notified' ? 'met ✓' : rem.waiting ? '—' : rem.text}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </>
  );
}
